import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import TranslateText from "../../components/TranslateText";
import { get_all_staff, update_query_detail } from "../../reduxData/user/userAction";
import "../../css/contact.css";
import { formatDateTime } from "../../hooks/formatDateTime";

const QueryDetail = () => {
    const { t } = useTranslation();
    const { userPermissions } = useSelector((state) => state.auth);
    const canEditQuery = userPermissions?.queries?.edit;
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const queryState = location.state?.data;
    const { staffList } = useSelector((state) => state.user);
    const { user } = useSelector((state) => state.auth);
    const [queryData, setQueryData] = useState(queryState || null);
    const [search, setSearch] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        assign_to: queryState?.assign_to || "",
        status: queryState?.status || "PENDING",
        priority: queryState?.priority || "LOW",
    });
    const [selectedStaff, setSelectedStaff] = useState(
        queryState?.assign_to
            ? {
                id: queryState.assign_to,
                user_name: queryState.assigned_to_name,
                email: queryState.assigned_to_email,
            }
            : null
    );

    useEffect(() => {
        get_all_staff(dispatch, 1, 5, search, user?.id);
    }, [dispatch, search, user?.id]);

    useEffect(() => {
        if (!queryState) return;
        setQueryData(queryState);
        setFormData({
            assign_to: queryState?.assign_to || "",
            status: queryState?.status || "PENDING",
            priority: queryState?.priority || "LOW",
        });
        setSelectedStaff(
            queryState?.assign_to
                ? {
                    id: queryState.assign_to,
                    user_name: queryState.assigned_to_name,
                    email: queryState.assigned_to_email,
                }
                : null
        );
    }, [queryState]);

    const categoryMap = useMemo(() => ({
        GENERAL: t("queries.categories.generalInquiry"),
        ORDER: t("queries.categories.orderIssue"),
        PAYMENT: t("queries.categories.paymentProblem"),
        SHIPPING: t("queries.categories.shippingDelivery"),
        RETURN: t("queries.categories.refundDispute"),
        ACCOUNT: t("queries.categories.accountSupport"),
        TECHNICAL: t("queries.categories.technicalSupport"),
        OTHER: t("queries.categories.other"),
    }), [t]);

    const statusOptions = useMemo(() => ([
        { label: t("queries.statusValues.pending"), value: "PENDING" },
        { label: t("queries.statusValues.in_progress"), value: "IN_PROGRESS" },
        { label: t("queries.statusValues.resolved"), value: "RESOLVED" },
        // { label: t("queries.statusValues.closed"), value: "CLOSED" },
    ]), [t]);

    const priorityOptions = useMemo(() => ([
        { label: t("queries.priorityValues.urgent"), value: "URGENT" },
        { label: t("queries.priorityValues.high"), value: "HIGH" },
        { label: t("queries.priorityValues.medium"), value: "MEDIUM" },
        { label: t("queries.priorityValues.low"), value: "LOW" },
    ]), [t]);

    const getStatusLabel = (value) => statusOptions.find((option) => option.value === String(value || "PENDING").toUpperCase())?.label || formatValue(value);
    const getPriorityLabel = (value) => priorityOptions.find((option) => option.value === String(value || "LOW").toUpperCase())?.label || formatValue(value || "LOW");

    const assignedName = selectedStaff?.user_name || queryData?.assigned_to_name || queryData?.assigned_staff || t("queries.unassigned");
    const assignedEmail = selectedStaff?.email || queryData?.assigned_to_email || "";

    const getInitials = (name) => {
        if (!name) return "U";
        return name
            .split(" ")
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part.charAt(0).toUpperCase())
            .join("");
    };

    const formatValue = (value) => {
        if (!value) return "-";
        return String(value).replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const pickStaff = (staff) => {
        setSelectedStaff(staff);
        setFormData((prev) => ({ ...prev, assign_to: staff?.id || "" }));
    };

    const handleCancel = () => {
        setFormData({
            assign_to: queryData?.assign_to || "",
            status: queryData?.status || "PENDING",
            priority: queryData?.priority || "LOW",
        });
        setSelectedStaff(
            queryData?.assign_to
                ? {
                    id: queryData.assign_to,
                    user_name: queryData.assigned_to_name,
                    email: queryData.assigned_to_email,
                }
                : null
        );
        setSearch("");
        setIsEditing(false);
    };

    const handleSave = async () => {
        if (!queryData?.id) return;
        setIsSaving(true);
        const payload = {
            assign_to: formData.assign_to || null,
            status: formData.status,
            priority: formData.priority || "LOW",
        };
        const res = await update_query_detail(dispatch, queryData.id, payload);
        if (res) {

            setQueryData((prev) => ({
                ...prev,
                ...payload,
                assigned_to_name: selectedStaff?.user_name || prev?.assigned_to_name,
                assigned_to_email: selectedStaff?.email || prev?.assigned_to_email,
            }));
            setIsEditing(false);
            navigate("/queries")
        }
        setIsSaving(false);
    };

    if (!queryData) {
        return (
            <div className="query-detail-page">
                <div className="query-empty-state">
                    <h4>{t("queriesDetails.notFound")}</h4>
                    <p>{t("queriesDetails.openFromList")}</p>
                    <button className="btn common-button" onClick={() => navigate("/queries")}>
                        {t("queriesDetails.backToQueries")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="query-detail-page">
            <div className="query-detail-header">
                <button className="query-back-btn" onClick={() => navigate(-1)}>
                    <i className="fa-solid fa-arrow-left"></i>
                </button>
                <div className="query-title-wrap">
                    <span className="query-kicker">{t("queriesDetails.supportTicket")} #{queryData?.id}</span>
                    <TranslateText text={queryData?.subject} fallback={t("queriesDetails.customerQuery")} as="h3" />
                </div>
            </div>

            <div className="query-status-strip">
                <div className={`query-chip status-${String(queryData?.status).toLowerCase()}`}>
                    {getStatusLabel(queryData?.status)}
                </div>
                <div className={`query-chip priority-${String(queryData?.priority || "LOW").toLowerCase()}`}>
                    {getPriorityLabel(queryData?.priority)}
                </div>
                <div className="query-chip">
                    {categoryMap[queryData?.category] || formatValue(queryData?.category)}
                </div>
                <div className="query-chip muted">
                    {t("queriesDetails.created")} {formatDateTime(queryData?.created_at)}
                </div>
                <div className="query-chip muted">
                    {t("queriesDetails.updatedAt")} {formatDateTime(queryData?.updated_at)}
                </div>                
            </div>

            <div className="query-detail-grid">
                <section className="query-main-panel">
                    <div className="query-customer-card">
                        <div className="query-avatar">{getInitials(queryData?.name)}</div>
                        <div>
                            <h5>{queryData?.name || "-"}</h5>
                            <p>{queryData?.email || "-"}</p>
                        </div>
                    </div>

                    <div className="query-section">
                        <div className="query-section-title">
                            <h5>{t("queriesDetails.customerMessage")}</h5>
                        </div>
                        <div className="query-message-box">
                            <TranslateText text={queryData?.message} fallback={t("queriesDetails.noMessageAvailable")} />
                        </div>
                    </div>
                </section>

                <aside className="query-side-panel">
                    <div className="query-section compact">
                        <div className="query-section-title">
                            <div className="d-flex gap-2">
                                <i className="fa-solid fa-user-check"></i>
                                <h5>{t("queriesDetails.ticketManagement")}</h5>
                            </div>
                            {!isEditing && canEditQuery &&
                                <button className="btn common-button" onClick={() => setIsEditing(true)}>
                                <i className="fa-regular fa-pen-to-square me-2"></i>
                                    {t("common.edit")}
                                </button>
                            }
                        </div>

                        <label className="query-form-label">{t("queriesDetails.assignedStaff")}</label>
                        <div className="query-assignee-preview">
                            <div className="query-avatar small">{getInitials(assignedName)}</div>
                            <div>
                                <strong>{assignedName}</strong>
                                <span>{assignedEmail || t("queriesDetails.noStaffSelected")}</span>
                            </div>
                        </div>

                        {isEditing && (
                            <div className="query-staff-picker">
                                <div className="query-search-wrap">
                                    <i className="fa-solid fa-magnifying-glass"></i>
                                    <input
                                        type="text"
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={t("queriesDetails.searchStaff")}
                                    />
                                </div>
                                <div className="query-staff-list">
                                    {staffList?.length > 0 ? staffList.map((staff) => (
                                        <button
                                            type="button"
                                            className={`query-staff-option ${String(formData.assign_to) === String(staff?.id) ? "active" : ""}`}
                                            key={staff?.id}
                                            onClick={() => pickStaff(staff)}
                                        >
                                            <span className="query-avatar mini">{getInitials(staff?.user_name)}</span>
                                            <span>
                                                <strong>{staff?.user_name || "-"}</strong>
                                                <small>{staff?.email || "-"}</small>
                                            </span>
                                        </button>
                                    )) : (
                                        <div className="query-no-staff">{t("queriesDetails.noStaffFound")}</div>
                                    )}
                                </div>
                            </div>
                        )}

                        <label className="query-form-label">{t("queriesDetails.ticketStatus")}</label>
                        {isEditing ? (
                            <select
                                className="form-select query-select"
                                value={formData.status}
                                onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.value }))}
                            >
                                {statusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        ) : (
                            <div className="query-readonly-field">{getStatusLabel(queryData?.status)}</div>
                        )}

                        <label className="query-form-label">{t("queriesDetails.priority")}</label>
                        {isEditing ? (
                            <select
                                className="form-select query-select"
                                value={formData.priority}
                                onChange={(e) => setFormData((prev) => ({ ...prev, priority: e.target.value }))}
                            >
                                {priorityOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        ) : (
                            <div className="query-readonly-field">{getPriorityLabel(queryData?.priority)}</div>
                        )}
                        <div className="query-header-actions">
                            {isEditing &&
                                <>
                                    <button className="btn btn-light" onClick={handleCancel} disabled={isSaving}>
                                        {t("queriesDetails.cancel")}
                                    </button>
                                    <button className="btn common-button" onClick={handleSave} disabled={isSaving}>
                                        {isSaving ? t("queriesDetails.saving") : t("queriesDetails.saveChanges")}
                                    </button>
                                </>}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default QueryDetail;
